"use client"

import * as React from "react"
import { Link } from "react-router"
import { Button } from "@/components/ui/button"
import { CardHeader, CardTitle } from "@/components/ui/card"
import { useProduct } from "@/contexts/product-context"
import { EVAL_TERM_CARD_TITLE_CLASS } from "@/lib/evaluation-dashboard-a11y"
import { evaluationTermDetailsPath } from "@/lib/evaluation-routes"
import { formatTermDateRange } from "@/lib/evaluation-term-utils"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"

interface EvaluationTermCardHeaderProps {
  term: EvaluationTerm
  /** Timeline chip, needs attention, etc. — sits beside the term name. */
  trailing?: React.ReactNode
  /** Actions aligned to the right of the header row (e.g. View details). */
  actions?: React.ReactNode
}

export function EvaluationTermViewDetailsButton({ term }: { term: EvaluationTerm }) {
  const { product } = useProduct()

  return (
    <Button size="sm" variant="ghost" asChild>
      <Link to={evaluationTermDetailsPath(product, term.id)}>View details</Link>
    </Button>
  )
}

/** Shared card header: H3 term name + academic year and dates on the meta line. */
export function EvaluationTermCardHeader({ term, trailing, actions }: EvaluationTermCardHeaderProps) {
  const dateRange = formatTermDateRange(term.startDate, term.endDate)

  return (
    <CardHeader className="pb-2">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className={EVAL_TERM_CARD_TITLE_CLASS}>{term.termName}</CardTitle>
            {trailing}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {term.academicYear} · {dateRange}
          </p>
        </div>
        {actions ? <div className="flex shrink-0 items-center">{actions}</div> : null}
      </div>
    </CardHeader>
  )
}
